import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Shield, Gauge, X } from 'lucide-react';

const OUTCOME_LABEL = {
  steward_may_decide: 'Steward may decide',
  steward_must_route: 'Steward must route',
  independent_approval_required: 'Independent approval required',
};

function DecisionPolicyForm({ templates, scopeTypes, onSave, onClose }) {
  const [f, setF] = useState({
    name: '', template: 'steward_led', scopeType: 'default', scopeValue: '',
    approver_role: 'Pricing Leadership',
  });
  const [busy, setBusy] = useState(false);
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4" data-testid="decision-policy-form">
      <div className="w-full max-w-md rounded-2xl border border-border/60 bg-background shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-border/40">
          <h4 className="font-semibold text-foreground">New decision policy</h4>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Name</label>
            <input data-testid="dp-name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} className="w-full mt-1 h-9 px-3 rounded-lg bg-muted/40 border border-border/40 text-sm text-foreground outline-none focus:border-primary/40" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Template</label>
            <select data-testid="dp-template" value={f.template} onChange={e => setF({ ...f, template: e.target.value })} className="w-full mt-1 h-9 px-3 rounded-lg bg-muted/40 border border-border/40 text-sm text-foreground">
              {templates.map(t => <option key={t.template} value={t.template}>{t.name}</option>)}
            </select>
            <p className="text-[11px] text-muted-foreground mt-1">{(templates.find(t => t.template === f.template) || {}).description}</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Scope</label>
              <select data-testid="dp-scope-type" value={f.scopeType} onChange={e => setF({ ...f, scopeType: e.target.value })} className="w-full mt-1 h-9 px-3 rounded-lg bg-muted/40 border border-border/40 text-sm text-foreground">
                {scopeTypes.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            {f.scopeType !== 'default' && (
              <div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Value</label>
                <input data-testid="dp-scope-value" value={f.scopeValue} onChange={e => setF({ ...f, scopeValue: e.target.value })} placeholder={f.scopeType === 'impact' ? 'high / normal' : 'e.g. pricing'} className="w-full mt-1 h-9 px-3 rounded-lg bg-muted/40 border border-border/40 text-sm text-foreground outline-none" />
              </div>
            )}
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Approver role</label>
            <input data-testid="dp-approver" value={f.approver_role} onChange={e => setF({ ...f, approver_role: e.target.value })} className="w-full mt-1 h-9 px-3 rounded-lg bg-muted/40 border border-border/40 text-sm text-foreground outline-none" />
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-border/40">
          <button onClick={onClose} className="px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted/50">Cancel</button>
          <button
            data-testid="dp-save"
            disabled={busy || !f.name.trim()}
            onClick={async () => {
              setBusy(true);
              try {
                await onSave({ name: f.name, template: f.template, approver_role: f.approver_role,
                  scope: { type: f.scopeType, value: f.scopeValue } });
              } finally { setBusy(false); }
            }}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >{busy ? 'Saving…' : 'Create policy'}</button>
        </div>
      </div>
    </div>
  );
}

function PriorityPolicyForm({ templates, onSave, onClose }) {
  const [f, setF] = useState({ name: '', template: 'balanced' });
  const [busy, setBusy] = useState(false);
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4" data-testid="priority-policy-form">
      <div className="w-full max-w-md rounded-2xl border border-border/60 bg-background shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-border/40">
          <h4 className="font-semibold text-foreground">New priority policy</h4>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Name</label>
            <input data-testid="pp-name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} className="w-full mt-1 h-9 px-3 rounded-lg bg-muted/40 border border-border/40 text-sm text-foreground outline-none focus:border-primary/40" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Template</label>
            <select data-testid="pp-template" value={f.template} onChange={e => setF({ ...f, template: e.target.value })} className="w-full mt-1 h-9 px-3 rounded-lg bg-muted/40 border border-border/40 text-sm text-foreground">
              {templates.map(t => <option key={t.template} value={t.template}>{t.name}</option>)}
            </select>
            <p className="text-[11px] text-muted-foreground mt-1">{(templates.find(t => t.template === f.template) || {}).description}</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-border/40">
          <button onClick={onClose} className="px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted/50">Cancel</button>
          <button data-testid="pp-save" disabled={busy || !f.name.trim()}
            onClick={async () => { setBusy(true); try { await onSave({ name: f.name, template: f.template }); } finally { setBusy(false); } }}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >{busy ? 'Saving…' : 'Create policy'}</button>
        </div>
      </div>
    </div>
  );
}

export default function DecisionPoliciesPage() {
  const [tab, setTab] = useState('decision');
  const [loading, setLoading] = useState(true);
  const [dPolicies, setDPolicies] = useState([]);
  const [pPolicies, setPPolicies] = useState([]);
  const [dTemplates, setDTemplates] = useState([]);
  const [scopeTypes, setScopeTypes] = useState([]);
  const [pTemplates, setPTemplates] = useState([]);
  const [showDForm, setShowDForm] = useState(false);
  const [showPForm, setShowPForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dl, pl, dt, pt] = await Promise.all([
        api.listDecisionPolicies(), api.listPriorityPolicies(),
        api.getDecisionPolicyTemplates(), api.getPriorityPolicyTemplates(),
      ]);
      setDPolicies(dl.policies || []);
      setPPolicies(pl.policies || []);
      setDTemplates(dt.templates || []);
      setScopeTypes(dt.scope_types || []);
      setPTemplates(pt.templates || []);
    } catch (e) {
      toast.error(e.message || 'Could not load policies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createDecision = async (body) => {
    try { await api.createDecisionPolicy(body); toast.success('Decision policy created'); setShowDForm(false); await load(); }
    catch (e) { toast.error(e.message); }
  };
  const createPriority = async (body) => {
    try { await api.createPriorityPolicy(body); toast.success('Priority policy created'); setShowPForm(false); await load(); }
    catch (e) { toast.error(e.message); }
  };
  const delDecision = async (id) => { try { await api.deleteDecisionPolicy(id); toast.success('Deleted'); await load(); } catch (e) { toast.error(e.message); } };
  const delPriority = async (id) => { try { await api.deletePriorityPolicy(id); toast.success('Deleted'); await load(); } catch (e) { toast.error(e.message); } };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="decision-policies-page">
      <h1 className="text-3xl font-bold text-foreground tracking-tight">Policies</h1>
      <p className="text-sm text-muted-foreground mt-1">
        Decision policies set who may decide; priority policies set when work should be handled. They are kept separate.
      </p>

      <div className="flex gap-2 mt-6 mb-5">
        <button onClick={() => setTab('decision')} data-testid="tab-decision"
          className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium border ${tab === 'decision' ? 'bg-primary/15 text-primary border-primary/30' : 'text-muted-foreground border-border/50 hover:bg-muted/40'}`}>
          <Shield className="w-4 h-4" /> Decision policies
        </button>
        <button onClick={() => setTab('priority')} data-testid="tab-priority"
          className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium border ${tab === 'priority' ? 'bg-primary/15 text-primary border-primary/30' : 'text-muted-foreground border-border/50 hover:bg-muted/40'}`}>
          <Gauge className="w-4 h-4" /> Priority policies
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : tab === 'decision' ? (
        <div>
          <div className="flex justify-end mb-3">
            <button onClick={() => setShowDForm(true)} data-testid="add-decision-policy" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90">
              <Plus className="w-4 h-4" /> New policy
            </button>
          </div>
          <div className="space-y-3" data-testid="decision-policy-list">
            {dPolicies.map(p => (
              <div key={p.policy_id} className="rounded-2xl border border-border/50 bg-card/40 p-4 flex items-start justify-between gap-3" data-testid={`decision-policy-${p.policy_id}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold text-foreground">{p.name}</h3>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground">v{p.version}</span>
                  </div>
                  <p className="text-[13px] text-muted-foreground mt-1">
                    Scope: <strong>{(p.scope || {}).type}{(p.scope || {}).value ? ` = ${p.scope.value}` : ''}</strong> · Approver: {p.approver_role}
                  </p>
                  <p className="text-[12px] text-muted-foreground/80 mt-1">
                    Default → {OUTCOME_LABEL[(p.rules || {}).default] || (p.rules || {}).default} · High impact → {OUTCOME_LABEL[(p.rules || {}).high_impact] || (p.rules || {}).high_impact}
                  </p>
                </div>
                <button onClick={() => delDecision(p.policy_id)} data-testid={`delete-decision-${p.policy_id}`} className="text-muted-foreground hover:text-rose-400 p-1"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <div className="flex justify-end mb-3">
            <button onClick={() => setShowPForm(true)} data-testid="add-priority-policy" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90">
              <Plus className="w-4 h-4" /> New policy
            </button>
          </div>
          <div className="space-y-3" data-testid="priority-policy-list">
            {pPolicies.map(p => (
              <div key={p.policy_id} className="rounded-2xl border border-border/50 bg-card/40 p-4 flex items-start justify-between gap-3" data-testid={`priority-policy-${p.policy_id}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold text-foreground">{p.name}</h3>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground">v{p.version}</span>
                  </div>
                  <p className="text-[13px] text-muted-foreground mt-1">Template: {p.template}</p>
                </div>
                <button onClick={() => delPriority(p.policy_id)} data-testid={`delete-priority-${p.policy_id}`} className="text-muted-foreground hover:text-rose-400 p-1"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showDForm && <DecisionPolicyForm templates={dTemplates} scopeTypes={scopeTypes} onSave={createDecision} onClose={() => setShowDForm(false)} />}
      {showPForm && <PriorityPolicyForm templates={pTemplates} onSave={createPriority} onClose={() => setShowPForm(false)} />}
    </div>
  );
}
